import axios from "axios";

// Determine the base URL based on environment
// For remote connections, we need the full URL to the server
const baseURL =
  process.env.REACT_APP_API_URL ||
  // In development, use relative path
  (process.env.NODE_ENV === "development"
    ? "/api"
    : // In production, try to determine server location
    // This assumes frontend and backend are on the same server
    `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ""
    }/api`);

console.log("API baseURL:", baseURL);

// Create axios instance with base URL and default headers
const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  // Add timeout to prevent hanging requests
  timeout: 15000,
  // Enable withCredentials for proper CORS handling with cookies if needed
  withCredentials: true,
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log outgoing requests for debugging
    console.log(
      `API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url
      }`
    );
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    console.log(`API Response: ${response.status} from ${response.config.url}`);
    return response;
  },
  (error) => {
    // Get response from error object
    const { response } = error;
    const originalRequest = error.config;

    // Handle token expiration
    if (response && response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      // Redirect to login if not already there
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    // Log the error for debugging
    console.error(
      `API Error: ${response ? response.status : "Network"} - ${error.message}`,
      originalRequest
        ? `URL: ${originalRequest.baseURL}${originalRequest.url}`
        : "",
      error
    );

    return Promise.reject(error);
  }
);

// Authentication Service
const authService = {
  login: (credentials) => apiClient.post("/auth/login", credentials),
  register: (userData) => apiClient.post("/auth/register", userData),
  getCurrentUser: () => apiClient.get("/auth/me"),
  updateProfile: (userData) => apiClient.put("/auth/profile", userData),
  changePassword: (passwordData) =>
    apiClient.put("/auth/password", passwordData),
  uploadProfileImage: (formData) =>
    apiClient.post("/auth/profile/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
};

// User Management Service (Admin only)
const userService = {
  getAll: (params) => apiClient.get("/users", { params }),
  getById: (id) => apiClient.get(`/users/${id}`),
  create: (userData) => apiClient.post("/users", userData),
  update: (id, userData) => apiClient.put(`/users/${id}`, userData),
  delete: (id) => apiClient.delete(`/users/${id}`),
  updateRole: (id, role) => apiClient.put(`/users/${id}/role`, { role }),
};

// Excavator Service
const excavatorService = {
  getAll: (params) => apiClient.get("/excavators", { params }),
  getById: (id) => apiClient.get(`/excavators/${id}`),
  create: (excavatorData) => apiClient.post("/excavators", excavatorData),
  update: (id, excavatorData) =>
    apiClient.put(`/excavators/${id}`, excavatorData),
  delete: (id) => apiClient.delete(`/excavators/${id}`),
  getStats: () => apiClient.get("/excavators/stats"),
  updateStatus: (id, status) =>
    apiClient.put(`/excavators/${id}/status`, { status }),
  getMaintenance: (id) =>
    apiClient.get(`/excavators/${id}/maintenance-history`),
  getMaintenanceHistory: (id) => apiClient.get(`/excavators/${id}/history`),
  uploadImage: (id, imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);
    return apiClient.post(`/excavators/${id}/image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  downloadPdf: (id) =>
    apiClient
      .get(`/excavators/${id}/pdf`, { responseType: "blob" })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `excavator-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return response;
      }),
  uploadAttachment: (id, formData) =>
    apiClient.post(`/excavators/${id}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
};

// Report Service
const reportService = {
  getAll: (params) => {
    // Handle potential null or undefined params
    const safeParams = params || {};
    return apiClient.get("/reports", { params: safeParams });
  },
  getById: (id) => apiClient.get(`/reports/${id}`),
  create: (reportData) => apiClient.post("/reports", reportData),
  update: (id, reportData) => apiClient.put(`/reports/${id}`, reportData),
  delete: (id) => apiClient.delete(`/reports/${id}`),
  getPdf: (id, options = {}) => {
    const requestOptions = {
      responseType: "blob",
    };

    // If language is specified, add it to the request params
    if (options.language) {
      requestOptions.params = { language: options.language };
    }

    return apiClient
      .get(`/reports/${id}/pdf`, requestOptions)
      .then((response) => {
        // If download option is true or not specified, handle download directly
        if (options.download !== false) {
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `report-${id}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }

        // Return response for further processing if needed
        return response;
      });
  },
  getStats: () => apiClient.get("/reports/stats"),
  assignTechnician: (id, technicianId) =>
    apiClient.put(`/reports/${id}/assign`, { technicianId }),
  updateStatus: (id, status, notes) =>
    apiClient.put(`/reports/${id}/status`, { status, notes }),
  approve: (id, approvalData) =>
    apiClient.put(`/reports/${id}/approve`, approvalData),
  addComment: (id, comment) =>
    apiClient.post(`/reports/${id}/comments`, { comment }),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append("attachment", file);
    return apiClient.post(`/reports/${id}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  bulkSign: (reportIds, userId, userName, signatureDataUrl) =>
    apiClient.post(`/reports/bulk-sign`, {
      reportIds,
      userId,
      userName,
      signatureDataUrl,
    }),
};

// BetriebstageBuch Service
const betriebstageBuchService = {
  getAll: (params) => apiClient.get("/betriebstagebuch", { params }),
  getById: (id) => apiClient.get(`/betriebstagebuch/${id}`),
  create: (data) => apiClient.post("/betriebstagebuch", data),
  update: (id, data) => apiClient.put(`/betriebstagebuch/${id}`, data),
  delete: (id) => apiClient.delete(`/betriebstagebuch/${id}`),
  approve: (id, approvalData) =>
    apiClient.put(`/betriebstagebuch/${id}/approve`, approvalData),
  getPdf: (id) =>
    apiClient
      .get(`/betriebstagebuch/${id}/pdf`, { responseType: "blob" })
      .then((response) => {
        // Create a blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `betriebstagebuch-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return response;
      }),
  // Added function to fetch unique creators
  getUniqueCreators: () => apiClient.get("/betriebstagebuch/unique-creators"),
};

// Dashboard Service
const dashboardService = {
  getStats: () => {
    return apiClient.get("/dashboard/stats").catch((error) => {
      console.warn(
        "Dashboard stats endpoint not available, using fallback data"
      );
      // Return fallback data with more comprehensive structure
      return {
        data: {
          totalExcavators: 0,
          activeExcavators: 0,
          totalReports: 0,
          pendingApprovalCount: 0,
          signedReports: 0,
          unsignedReports: 0,
          activeUsers: 0,
          totalBetriebstagebuch: 0,
          betriebstageBuchData: {
            total: 0,
            unsigned: 0,
          },
          maintenanceDueCount: 0,
          monthlyReportCounts: Array(6).fill({
            month: "",
            count: 0,
            maintenance: 0,
            inspection: 0,
          }),
          operatorStats: [],
          reportsByType: {},
          percentChange: {
            reports: 0,
            excavators: 0,
            maintenance: 0,
          },
        },
      };
    });
  },
  getRecentActivity: () => {
    return apiClient.get("/dashboard/recent-activity").catch((error) => {
      console.warn(
        "Recent activity endpoint not available, using fallback data"
      );
      return { data: [] };
    });
  },
  getPendingTasks: () => {
    return apiClient.get("/dashboard/pending-tasks").catch((error) => {
      console.warn("Pending tasks endpoint not available, using fallback data");
      return { data: [] };
    });
  },
  getInventorySummary: () => {
    return apiClient.get("/dashboard/inventory-summary").catch((error) => {
      console.warn("Inventory summary endpoint not available, using fallback data");
      return { data: [] }; // Return empty array on error
    });
  },
};

export {
  apiClient,
  authService,
  userService,
  excavatorService,
  reportService,
  dashboardService,
  betriebstageBuchService,
};
